import { getInitDataRaw } from '@/sdk/telegram';
import type {
  ApiError, CompanyComparison, DashboardView, Dispute, HotZonesView,
  OpenDisputeRequest, PageView, PersonalAudit, ProfileView, SubmissionDetails,
  SubmissionView, UpdateProfileRequest, WalletTransaction, WalletView,
  WithdrawRequest, WithdrawalResult,
} from './types';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly retryAfterSeconds?: number,
  ) {
    super(message);
  }
}

function authHeaders(): HeadersInit {
  const initData = getInitDataRaw();
  if (initData) return { 'X-Tg-Init-Data': initData };
  const devId = import.meta.env.VITE_DEV_TELEGRAM_ID;
  if (devId) return { 'X-Dev-Telegram-Id': devId };
  return {};
}

/** Returns the same headers the SSE EventSource would need (read-only). */
export function authHeadersForSse(): Record<string, string> {
  return authHeaders() as Record<string, string>;
}

async function parseError(res: Response): Promise<HttpError> {
  let body: ApiError | undefined;
  try { body = await res.json() as ApiError; } catch { /* ignore */ }
  const retryAfter = res.headers.get('Retry-After');
  return new HttpError(
    res.status,
    body?.code ?? 'http_error',
    body?.message ?? res.statusText,
    retryAfter ? Number(retryAfter) : undefined,
  );
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: { ...authHeaders(), ...(init.headers ?? {}) },
  });
  if (!res.ok) throw await parseError(res);
  // A Kotlin controller that returns null serialises as 200 with an empty body
  // (GET /api/analytics/companies does this for a user with no district data),
  // and 204 has no body either — res.json() throws on both.
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

function asJson(body: unknown): RequestInit {
  return {
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  };
}

export const api = {
  // ─── dashboard ───────────────────────────────────────────────
  getDashboard(): Promise<DashboardView> {
    return request('/api/dashboard');
  },

  // ─── submissions ─────────────────────────────────────────────
  listSubmissions(page = 0, size = 20): Promise<PageView<SubmissionView>> {
    return request(`/api/submissions?page=${page}&size=${size}`);
  },
  getSubmissionDetails(id: string): Promise<SubmissionDetails> {
    return request(`/api/submissions/${encodeURIComponent(id)}/details`);
  },
  createSubmission(input: {
    company: string;
    vehicleType: string;
    city?: string;
    district?: string;
    type?: 'DAILY' | 'HISTORICAL';
    longestTripKm?: number;
    shiftDate?: string;
    files: File[];
    idempotencyKey?: string;
  }): Promise<SubmissionView> {
    const fd = new FormData();
    fd.append('company', input.company);
    fd.append('vehicleType', input.vehicleType);
    if (input.city) fd.append('city', input.city);
    if (input.district) fd.append('district', input.district);
    if (input.type) fd.append('type', input.type);
    if (input.longestTripKm != null) fd.append('longestTripKm', String(input.longestTripKm));
    if (input.shiftDate) fd.append('shiftDate', input.shiftDate);
    for (const f of input.files) fd.append('files', f, f.name);
    const headers: Record<string, string> = {};
    if (input.idempotencyKey) headers['Idempotency-Key'] = input.idempotencyKey;
    return request('/api/submissions', { method: 'POST', body: fd, headers });
  },

  // ─── analytics ───────────────────────────────────────────────
  getHotZones(): Promise<HotZonesView> {
    return request('/api/analytics/hot');
  },
  getCompanyComparison(): Promise<CompanyComparison | null> {
    return request('/api/analytics/companies');
  },
  getPersonalAudit(): Promise<PersonalAudit> {
    return request('/api/analytics/me');
  },

  // ─── wallet ──────────────────────────────────────────────────
  getWallet(): Promise<WalletView> {
    return request('/api/wallet');
  },
  listWalletTransactions(page = 0, size = 30): Promise<PageView<WalletTransaction>> {
    return request(`/api/wallet/transactions?page=${page}&size=${size}`);
  },
  withdraw(req: WithdrawRequest, idempotencyKey: string): Promise<WithdrawalResult> {
    return request('/api/wallet/withdraw', {
      method: 'POST',
      ...asJson(req),
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
    });
  },

  // ─── profile ─────────────────────────────────────────────────
  getProfile(): Promise<ProfileView> {
    return request('/api/profile');
  },
  updateProfile(req: UpdateProfileRequest): Promise<ProfileView> {
    return request('/api/profile', { method: 'PATCH', ...asJson(req) });
  },
  deleteProfile(): Promise<void> {
    return request('/api/profile', { method: 'DELETE' });
  },
  /** `contact` is the raw signed payload from requestContact() — the backend verifies it. */
  setPhoneFromTelegram(contact: string): Promise<ProfileView> {
    return request('/api/profile/phone/telegram', { method: 'POST', ...asJson({ contact }) });
  },

  // ─── disputes ────────────────────────────────────────────────
  openDispute(req: OpenDisputeRequest): Promise<Dispute> {
    return request('/api/disputes', { method: 'POST', ...asJson(req) });
  },
};

/**
 * Subscribes to the submission status SSE stream.
 *
 * The browser EventSource API cannot send custom headers, so we can't use it
 * directly (Telegram auth lives in X-Tg-Init-Data). Instead we use fetch +
 * ReadableStream to parse SSE manually.
 *
 * Returns an unsubscribe function. The stream auto-closes when the server
 * sends a terminal status (or after the server's 5-minute timeout).
 */
export function subscribeSubmissionStatus(
  id: string,
  onEvent: (e: { status: string; timestamp: string }) => void,
  onError?: (e: unknown) => void,
  /**
   * Called when the stream ends without an error — normal at the server's 5-minute timeout.
   * A submission can outlive that, so the caller needs the chance to re-subscribe.
   */
  onClose?: () => void,
): () => void {
  const ctrl = new AbortController();
  void (async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/submissions/${encodeURIComponent(id)}/stream`, {
        headers: { ...authHeaders(), Accept: 'text/event-stream' },
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) throw await parseError(res);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // SSE events are separated by blank lines (\n\n).
        let idx: number;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const raw = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const dataLine = raw.split('\n').find((l) => l.startsWith('data:'));
          if (!dataLine) continue;
          try {
            const parsed = JSON.parse(dataLine.slice(5).trim());
            onEvent(parsed);
          } catch {
            // skip malformed
          }
        }
      }
      if (!ctrl.signal.aborted) onClose?.();
    } catch (err) {
      if ((err as Error).name !== 'AbortError') onError?.(err);
    }
  })();
  return () => ctrl.abort();
}
